import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Card, Col, Row, Progress, Skeleton, Space, Statistic, Tag, Typography } from 'antd';
import {
  ArrowRightOutlined,
  AuditOutlined,
  CreditCardOutlined,
  ReloadOutlined,
  RiseOutlined,
  SafetyCertificateOutlined,
  SoundOutlined,
  SwapOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import { StaffRole, SystemStatsResponse, UserStatus, KYCReviewTaskStatus } from '@mygate/common';
import { api } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import { transactionQueueService } from '@/services/transactionQueue';
import { cardRenewalService } from '@/services/cardRenewal';
import { subscriptionService } from '@/services/subscription';
import { kycReviewTasksService } from '@/services/kycReviewTasks';
import { DashboardAnalytics } from '@/components/DashboardAnalytics';
import { usersService } from '@/services/users';

const { Title, Text, Paragraph } = Typography;

const quickActions = [
  { path: '/users', title: 'Kunden', text: 'Profile und Konten verwalten', icon: <TeamOutlined />, admin: false },
  {
    path: '/kyc-manual-review',
    title: 'KYC prüfen',
    text: 'Manuelle Prüfungen bearbeiten',
    icon: <AuditOutlined />,
    admin: true,
  },
  { path: '/transaction-queue', title: 'Zahlungen', text: 'Warteschlange öffnen', icon: <SwapOutlined />, admin: true },
  { path: '/broadcasts', title: 'Broadcasts', text: 'Kunden gezielt erreichen', icon: <SoundOutlined />, admin: true },
];

// Landing on the dashboard again within a minute reuses the numbers instead of re-running every list query
// (the onboarding count is a full user-list request); "Aktualisieren" still refetches on demand.
const DASHBOARD_STALE_TIME = 60_000;

function OperationsOverview({ staffId }: { staffId: string }) {
  const [analyticsThrough, setAnalyticsThrough] = useState(() => new Date().toISOString());
  const queue = useQuery({
    queryKey: ['dashboard', staffId, 'queue'],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: async () => {
      const data = await transactionQueueService.getStatistics();
      if (!data) throw new Error('Keine Zahlungsstatistik verfügbar');
      return data;
    },
  });
  const renewals = useQuery({
    queryKey: ['dashboard', staffId, 'renewals'],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: async () => {
      const response = await cardRenewalService.getStatistics();
      if (!response.data) throw new Error('Keine Kartenstatistik verfügbar');
      return response.data;
    },
  });
  const subscriptions = useQuery({
    queryKey: ['dashboard', staffId, 'subscriptions'],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: async () => {
      const response = await subscriptionService.getStatistics();
      if (!response.data) throw new Error('Keine Abostatistik verfügbar');
      return response.data;
    },
  });
  const customers = useQuery({
    queryKey: ['dashboard', staffId, 'customers'],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: async () => {
      const response = await api.get<SystemStatsResponse>('/api/staff/system/stats');
      if (!response.data) throw new Error('Keine Kundenstatistik verfügbar');
      return response.data;
    },
  });
  const onboarded = useQuery({
    queryKey: ['dashboard', staffId, 'onboarded'],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: async () => {
      const response = await usersService.fetchUsers({ tradevestBucket: 'COMPLETE', pageSize: 1, page: 1 });
      if (!response.data) throw new Error('Keine Onboardingstatistik verfügbar');
      return response.data.total;
    },
  });
  const kycReviews = useQuery({
    queryKey: ['dashboard', staffId, 'kyc-reviews'],
    staleTime: DASHBOARD_STALE_TIME,
    queryFn: async () => {
      const [open, inProgress] = await Promise.all([
        kycReviewTasksService.fetchReviewTasks({ status: KYCReviewTaskStatus.OPEN, page: 1, limit: 1 }),
        kycReviewTasksService.fetchReviewTasks({ status: KYCReviewTaskStatus.IN_PROGRESS, page: 1, limit: 1 }),
      ]);
      if (!open || !inProgress) throw new Error('Keine KYC-Prüfstatistik verfügbar');
      return { open: open.total, inProgress: inProgress.total, total: open.total + inProgress.total };
    },
  });
  const totalCustomers = customers.data?.usersByStatus.reduce((sum, item) => sum + item.count, 0);
  const statusCount = (status: UserStatus) =>
    customers.data ? (customers.data.usersByStatus.find(item => item._id === status)?.count ?? 0) : undefined;
  const activeCustomers = statusCount(UserStatus.ACTIVE);
  const refreshing = [queue, renewals, subscriptions, customers, onboarded, kycReviews].some(query => query.isFetching);
  const hasError = [queue, renewals, subscriptions, customers, onboarded, kycReviews].some(query => query.isError);
  const metrics = [
    {
      title: 'Registrierte Kunden',
      value: totalCustomers,
      description: 'Gesamter aktueller Kundenbestand',
      link: '/users',
      linkLabel: 'Kunden öffnen',
      icon: <TeamOutlined />,
      tone: 'teal',
      query: customers,
    },
    {
      title: 'Neue Registrierungen',
      value: customers.data?.recentRegistrations,
      description: 'In den letzten 7 Tagen',
      link: '/users',
      linkLabel: 'Registrierungen ansehen',
      icon: <RiseOutlined />,
      tone: 'blue',
      query: customers,
    },
    {
      title: 'Verrechnungs- & Kryptokonten · gesamt',
      value: onboarded.data,
      description: 'Gesamtbestand inkl. Test- und Sandbox-Accounts',
      link: '/users',
      linkLabel: 'Kunden öffnen',
      icon: <SafetyCertificateOutlined />,
      tone: 'teal',
      query: onboarded,
    },
    {
      title: 'Aktive Abos',
      value: subscriptions.data?.activeSubscriptions,
      description: 'Aktuell aktive Abonnements',
      link: '/subscriptions',
      linkLabel: 'Abos öffnen',
      icon: <CreditCardOutlined />,
      tone: 'blue',
      query: subscriptions,
    },
  ];

  return (
    <>
      <div className="dashboard-section-heading">
        <Title level={4}>Wachstum & Onboarding</Title>
        <Button
          icon={<ReloadOutlined />}
          loading={refreshing}
          onClick={() => {
            setAnalyticsThrough(new Date().toISOString());
            void queue.refetch();
            void renewals.refetch();
            void subscriptions.refetch();
            void customers.refetch();
            void onboarded.refetch();
            void kycReviews.refetch();
          }}
        >
          Aktualisieren
        </Button>
      </div>
      {hasError && (
        <Alert
          className="dashboard-alert"
          type="warning"
          showIcon
          message="Ein Teil der Kennzahlen konnte nicht geladen werden."
          description="Die betroffenen Karten sind gekennzeichnet. Versuche es mit Aktualisieren erneut."
        />
      )}
      <Row gutter={[16, 16]}>
        {metrics.map(metric => (
          <Col key={metric.title} xs={24} sm={12} lg={6}>
            <Card className="dashboard-metric">
              <div className="dashboard-metric-heading">
                <Text type="secondary">{metric.title}</Text>
                <span className={`dashboard-icon dashboard-icon--${metric.tone}`}>{metric.icon}</span>
              </div>
              {metric.query.isPending ? (
                <Skeleton.Input active size="large" />
              ) : (
                <Statistic
                  value={metric.query.isError || metric.value === undefined ? '—' : metric.value}
                  groupSeparator="."
                />
              )}
              <Paragraph type={metric.query.isError ? 'danger' : 'secondary'} className="dashboard-metric-description">
                {metric.query.isError ? 'Nicht verfügbar' : metric.description}
              </Paragraph>
              <Link to={metric.link} className="dashboard-card-link">
                {metric.linkLabel} <ArrowRightOutlined />
              </Link>
              <div className="dashboard-updated">
                {metric.query.dataUpdatedAt > 0 && !metric.query.isError && (
                  <Text type="secondary">
                    Stand:{' '}
                    {new Date(metric.query.dataUpdatedAt).toLocaleTimeString('de-DE', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                )}
              </div>
            </Card>
          </Col>
        ))}
      </Row>
      <DashboardAnalytics through={analyticsThrough} />
      <Row gutter={[20, 20]} className="dashboard-followup">
        <Col xs={24} lg={12}>
          <Card title="Aktivierung der Kunden" className="dashboard-status-card">
            <Text type="secondary">Aktueller Bestand · Anteil an allen registrierten Kunden</Text>
            {customers.isPending ? (
              <Skeleton active paragraph={{ rows: 3 }} />
            ) : customers.isError ? (
              <Text type="danger">Nicht verfügbar</Text>
            ) : (
              <>
                <div className="dashboard-activation-heading">
                  <Statistic title="Aktivierte Kunden" value={activeCustomers ?? '—'} groupSeparator="." />
                  <Text type="secondary">von {totalCustomers?.toLocaleString('de-DE')}</Text>
                </div>
                <Progress
                  percent={totalCustomers ? Math.round(((activeCustomers ?? 0) / totalCustomers) * 1000) / 10 : 0}
                  strokeColor="#087f79"
                  format={percent => (totalCustomers ? `${percent?.toLocaleString('de-DE')} %` : '—')}
                />
                <Text type="secondary">Kontostatus aktiv; keine Aussage zur App-Nutzung.</Text>
                <div className="dashboard-status-list">
                  {[
                    { label: UserStatus.PENDING_VERIFICATION, status: UserStatus.PENDING_VERIFICATION },
                    { label: UserStatus.KYC_PROCESSING, status: UserStatus.KYC_PROCESSING },
                    { label: UserStatus.MANUAL_REVIEW, status: UserStatus.MANUAL_REVIEW },
                    { label: UserStatus.SUSPENDED, status: UserStatus.SUSPENDED },
                    { label: UserStatus.DELETION_PENDING, status: UserStatus.DELETION_PENDING },
                  ].map(item => (
                    <div className="dashboard-followup-link" key={item.status}>
                      <Text>{item.label}</Text>
                      <Text strong>{statusCount(item.status)?.toLocaleString('de-DE')}</Text>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Offene Aufgaben" className="dashboard-status-card">
            <Text type="secondary">Aktuell wartende Vorgänge im Tagesgeschäft</Text>
            <div className="dashboard-status-list">
              <div className="dashboard-priority-task">
                <div className="dashboard-followup-link">
                  <Text strong>Manuelle KYC-Prüfungen</Text>
                  <Tag color={!kycReviews.isError && (kycReviews.data?.total ?? 0) > 0 ? 'warning' : 'default'}>
                    {kycReviews.isPending
                      ? 'Lädt …'
                      : kycReviews.isError
                        ? 'Nicht verfügbar'
                        : (kycReviews.data?.total ?? '—')}
                  </Tag>
                </div>
                <Space wrap size={[16, 8]}>
                  <Link to="/kyc-manual-review?status=OPEN">
                    OPEN: {kycReviews.isPending || kycReviews.isError ? '—' : kycReviews.data?.open}{' '}
                    <ArrowRightOutlined />
                  </Link>
                  <Link to="/kyc-manual-review?status=IN_PROGRESS">
                    IN_PROGRESS: {kycReviews.isPending || kycReviews.isError ? '—' : kycReviews.data?.inProgress}{' '}
                    <ArrowRightOutlined />
                  </Link>
                </Space>
              </div>
              {[
                {
                  name: 'Zahlungen zur Freigabe',
                  count: queue.data?.pending,
                  query: queue,
                  path: '/transaction-queue',
                },
                {
                  name: 'Kartenverlängerungen zur Prüfung',
                  count: renewals.data?.draft,
                  query: renewals,
                  path: '/card-renewals',
                },
                {
                  name: 'Ausstehende Belastungen',
                  count: subscriptions.data?.pendingCharges,
                  query: subscriptions,
                  path: '/subscriptions',
                },
              ].map(item => (
                <Link to={item.path} className="dashboard-followup-link" key={item.name}>
                  <Text>{item.name}</Text>
                  <Space>
                    <Tag bordered={false}>
                      {item.query.isPending ? 'Lädt …' : item.query.isError ? 'Nicht verfügbar' : (item.count ?? '—')}
                    </Tag>
                    <ArrowRightOutlined />
                  </Space>
                </Link>
              ))}
            </div>
          </Card>
        </Col>
      </Row>
      <Card className="dashboard-followup" title="Fehlgeschlagene Vorgänge · Gesamtbestand">
        <Row gutter={[24, 16]}>
          {[
            { name: 'Zahlungen', count: queue.data?.failed, query: queue, path: '/transaction-queue' },
            { name: 'Kartenverlängerungen', count: renewals.data?.failed, query: renewals, path: '/card-renewals' },
            {
              name: 'Abrechnungen',
              count: subscriptions.data?.failedCharges,
              query: subscriptions,
              path: '/subscriptions',
            },
          ].map(item => (
            <Col xs={24} md={8} key={item.name}>
              <Link to={item.path} className="dashboard-followup-link">
                <Text>{item.name}</Text>
                <Space>
                  <Tag bordered={false} color={!item.query.isError && (item.count ?? 0) > 0 ? 'error' : 'default'}>
                    {item.query.isPending ? 'Lädt …' : item.query.isError ? 'Nicht verfügbar' : (item.count ?? '—')}
                  </Tag>
                  <ArrowRightOutlined />
                </Space>
              </Link>
            </Col>
          ))}
        </Row>
      </Card>
    </>
  );
}

export function Dashboard() {
  const staff = useAuthStore(state => state.staff);
  const isAdmin = staff?.role === StaffRole.ADMIN;

  return (
    <div className="dashboard-page">
      <div className="dashboard-intro">
        <Text type="secondary">mygate Backoffice</Text>
        <Title level={2}>Kunden & Wachstum im Blick</Title>
        <Paragraph type="secondary">
          {isAdmin
            ? 'Neue Kunden, eingerichtete Tradevest-Accounts und aktive Abos – mit dem aktuellen Handlungsbedarf darunter.'
            : 'Kundenprofile, KYC-Fälle und Kontodetails an einem Ort.'}
        </Paragraph>
      </div>
      {isAdmin && staff && <OperationsOverview key={staff.id} staffId={staff.id} />}
      <Card title="Schnellaktionen" className="dashboard-actions">
        <Row gutter={[16, 16]}>
          {quickActions
            .filter(action => !action.admin || isAdmin)
            .map(action => (
              <Col key={action.path} xs={24} sm={12} xl={6}>
                <Link to={action.path} className="dashboard-quick-action">
                  <span className="dashboard-icon dashboard-icon--teal">{action.icon}</span>
                  <div>
                    <Text strong>{action.title}</Text>
                    <Text type="secondary">{action.text}</Text>
                  </div>
                  <ArrowRightOutlined />
                </Link>
              </Col>
            ))}
        </Row>
      </Card>
    </div>
  );
}
